import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Home from "@/pages/Home";
import Blogs from "@/pages/Blogs";
import Videos from "@/pages/Videos";
import Worksheets from "@/pages/Worksheets";
import Courses from "@/pages/Courses";
import Contact from "@/pages/Contact";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { AuthProvider, useAuth } from "@/lib/auth";
import { MemberProvider } from "@/lib/member";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminCourses from "@/pages/admin/AdminCourses";
import AdminOffers from "@/pages/admin/AdminOffers";
import AdminBlogs from "@/pages/admin/AdminBlogs";
import AdminVideos from "@/pages/admin/AdminVideos";
import AdminWorksheets from "@/pages/admin/AdminWorksheets";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminMembers from "@/pages/admin/AdminMembers";
import Enroll from "@/pages/Enroll";
import MyAccount from "@/pages/MyAccount";
import AuthCallback from "@/pages/AuthCallback";

function PublicShell({ children }) {
  return (
    <>
      <Navbar />
      {children}
      <Footer />
    </>
  );
}

function RequireAuth({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#1F2547]">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/admin" replace />;
  }
  return children;
}

/**
 * Reads URL hash for OAuth session_id (from Emergent Google Auth callback)
 * and short-circuits to the AuthCallback page.
 */
function RootRouter() {
  const location = useLocation();
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<PublicShell><Home /></PublicShell>} />
      <Route path="/courses" element={<PublicShell><Courses /></PublicShell>} />
      <Route path="/blogs" element={<PublicShell><Blogs /></PublicShell>} />
      <Route path="/videos" element={<PublicShell><Videos /></PublicShell>} />
      <Route path="/worksheets" element={<PublicShell><Worksheets /></PublicShell>} />
      <Route path="/enroll" element={<PublicShell><Enroll /></PublicShell>} />
      <Route path="/contact" element={<PublicShell><Contact /></PublicShell>} />
      <Route path="/my-account" element={<PublicShell><MyAccount /></PublicShell>} />
      <Route path="/auth-callback" element={<AuthCallback />} />

      <Route path="/admin/login" element={<AdminLogin />} />
      <Route
        path="/admin"
        element={
          <RequireAuth>
            <AdminLayout />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="courses" replace />} />
        <Route path="courses" element={<RequireAuth roles={["admin"]}><AdminCourses /></RequireAuth>} />
        <Route path="leads" element={<RequireAuth roles={["admin"]}><AdminLeads /></RequireAuth>} />
        <Route path="members" element={<RequireAuth roles={["admin"]}><AdminMembers /></RequireAuth>} />
        <Route path="offers" element={<AdminOffers />} />
        <Route path="blogs" element={<RequireAuth roles={["admin"]}><AdminBlogs /></RequireAuth>} />
        <Route path="videos" element={<RequireAuth roles={["admin"]}><AdminVideos /></RequireAuth>} />
        <Route path="worksheets" element={<AdminWorksheets />} />
        <Route path="users" element={<RequireAuth roles={["admin"]}><AdminUsers /></RequireAuth>} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <MemberProvider>
            <RootRouter />
          </MemberProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
